import { defaultRangeExtractor, type Range } from '@tanstack/react-virtual';

import { ROW_DRAG_COLUMN } from '../RowOrder/RowOrder.js';

/**
 * Calculates the left distance for the left pinned prefix sticky columns.
 * @param columnIndex - the index of the pinned column.
 * @param pinnedColumns - the list of the pinned columns IDs.
 * @param rowDensity - the density of the rows.
 * @param isRowThresholdEnabled - determines whether thresholds for rows are enabled.
 * @returns The left distance in pixels.
 */
export function calculateLeftPinnedPosition(
  columnIndex: number,
  pinnedColumns: string[] | undefined,
  rowDensity: 'comfortable' | 'condensed' | 'default',
  isRowThresholdEnabled?: boolean,
): string | undefined {
  // if there are no pinned columns
  if (
    pinnedColumns === undefined ||
    pinnedColumns?.length === 0 ||
    columnIndex < 0 ||
    columnIndex > 2
  ) {
    return undefined;
  }
  const CONDENSED_PADDING = 2;
  const DEFAULT_PADDING = 8;
  const COMFORTABLE_PADDING = 12;
  const DRAG_HANDLE_SIZE = 24;
  const SELECTION_CHECKBOX_SIZE = 20;

  const thresholdPillSize = isRowThresholdEnabled ? 4 : 0;

  const paddingMap = {
    condensed: CONDENSED_PADDING,
    default: DEFAULT_PADDING,
    comfortable: COMFORTABLE_PADDING,
  };

  if (columnIndex === 0) {
    return '0px';
  }

  if (columnIndex === 1) {
    if (pinnedColumns[0] === ROW_DRAG_COLUMN.id) {
      // first column is row drag column
      return `${
        paddingMap[rowDensity] * 2 + DRAG_HANDLE_SIZE + thresholdPillSize
      }px`;
    } else {
      // first column is row selection column
      return `${
        paddingMap[rowDensity] * 2 + SELECTION_CHECKBOX_SIZE + thresholdPillSize
      }px`;
    }
  }

  if (columnIndex === 2) {
    // only row details column can be the third column, drag and selection column are first and second columns
    return `${
      paddingMap[rowDensity] * 3 +
      DRAG_HANDLE_SIZE +
      SELECTION_CHECKBOX_SIZE +
      thresholdPillSize
    }px`;
  }
}

/**
 * Adapts the visible range indexes to include pinned columns.
 *
 * This function is passed to the `rangeExtractor` of TanStack Virtualization.
 * It modifies the visible range indexes to ensure that pinned columns are preserved
 * within the range of visible columns.
 *
 * @param range - the array of indexes representing the initial visible range.
 * @param pinnedLeftIndexes - the list of indexes pinned to the left.
 * @param pinnedRightIndexes - the list of indexes pinned to the right.
 * @returns An array of indexes with pinned columns included in the visible range.
 */
export function addPinnedColumnsToVisibleRange(
  range: Range,
  pinnedLeftIndexes: number[],
  pinnedRightIndexes: number[],
): number[] {
  const defaultRange = [...defaultRangeExtractor(range)];

  if (pinnedLeftIndexes.length === 0 && pinnedRightIndexes.length === 0) {
    return defaultRange;
  }

  const newRange = [
    ...new Set([...pinnedLeftIndexes, ...defaultRange, ...pinnedRightIndexes]),
  ];

  return newRange;
}
